import React, { createContext, useEffect } from 'react';

import { AerosyncMfaWidget } from '../components/aerosync-mfa-widget';
import { AerosyncPreAuthContextType, AerosyncPreAuthProviderProps } from '../types';
import { usePreAuthReducer, PreAuthStep } from '../hooks/use-pre-auth-reducer';
import { usePreAuthProviderEvents } from '../hooks/use-pre-auth-provider-events';

export const AerosyncPreAuthContext = createContext<AerosyncPreAuthContextType | undefined>(undefined);

/**
 * AerosyncPreAuthProvider - Manages the entire Aerosync pre-authorization flow
 *
 * ## Overview
 * This provider orchestrates an Aerosync pre-authorization flow that determines if the balance check that occurs during the on submit mutation
 * requires Multi-Factor Authentication (MFA) before it can be checked.
 *
 * ## Pre-Auth Flow Steps
 * 1. **Trigger**: `triggerPreAuthFlow(paymentMethodId)` is called from checkout
 * 2. **GraphQL Check**: Sends `startPreAuthCheck` mutation to backend
 * 3. **Response Handling**:
 *    - `STATUS_READY`: Payment method is pre-authorized, proceed to submit order
 *    - `STATUS_PENDING`: Backend returns a Pusher channel name for real-time updates
 * 4. **Pusher Subscription**: Listen for real-time pre-auth status updates
 * 5. **Status Updates**:
 *    - `MFA_REQUIRED`: Show MFA widget to user
 *    - `STATUS_READY`: Pre-auth complete, proceed to checkout
 *    - Other statuses: Fail forward (allow checkout to continue)
 *
 * ## MFA Widget Flow
 * When MFA is required:
 * 1. `AerosyncMfaWidget` is rendered with credentials from Pusher event
 * 2. User completes MFA in the Aerosync widget
 * 3. On success: Waits for "STATUS_READY" Pusher event to complete pre-auth
 * 4. On failure/close: Reject the pre-auth promise with user-facing error
 *
 * ## State Management
 * - **usePreAuthReducer**: Manages current step (idle, waiting, mfaRequired) and MFA props
 * - **usePreAuthProviderEvents**: Handles all event callbacks and business logic
 * - **Pusher**: Real-time communication for pre-auth status updates
 * - **Promise-based**: Returns a Promise to calling code that resolves when flow completes
 *
 * ## Error Handling & Fail Forward
 * - **Fail Forward**: On pusher timeouts, allow checkout to proceed normally
 * - **User Errors**: Show user-facing error messages for actionable issues (MFA required)
 * - **System Errors**: Log to DataDog but fail forward to prevent checkout blocking
 * - **Timeout**: 10-second timeout for pusher events with automatic fail forward
 *
 * ## Key Integrations
 * - **GraphQL**: `startPreAuthCheck` mutation
 * - **Pusher**: Real-time status updates via WebSocket
 * - **Aerosync SDK**: MFA widget rendering and callbacks
 * - **Analytics**: Dutchie Pay event tracking throughout flow
 * - **DataDog**: Error logging and monitoring
 *
 * ## Usage Example
 * ```typescript
 * // 1. Wrap checkout with provider
 * <AerosyncPreAuthProvider>
 *   <CheckoutForm />
 * </AerosyncPreAuthProvider>
 *
 * // 2. Use in checkout submission
 * const { triggerPreAuthFlow } = useAerosyncPreAuthContext();
 *
 * try {
 *   await triggerPreAuthFlow(paymentMethodId);
 *   // Pre-auth complete, proceed with checkout
 * } catch (error) {
 *   // Handle pre-auth failure (user cancelled MFA, etc.)
 * }
 * ```
 */
export const AerosyncPreAuthProvider: React.FC<AerosyncPreAuthProviderProps> = ({ children }) => {
  const [{ mfaProps, step, channelName }, dispatch] = usePreAuthReducer();

  const { cleanup, triggerPreAuthFlow, handleMfaSuccess, handleMfaClose, handleMfaError } = usePreAuthProviderEvents({
    channelName,
    dispatch,
  });

  const validMfaProps = !!mfaProps?.aeroPassUserUuid && !!mfaProps.token && !!mfaProps.jobId && !!mfaProps.connectionId;

  useEffect(() => () => cleanup(), [cleanup]);

  const contextValue: AerosyncPreAuthContextType = {
    triggerPreAuthFlow,
  };

  return (
    <AerosyncPreAuthContext.Provider value={contextValue}>
      {step === PreAuthStep.mfaRequired && validMfaProps && (
        <AerosyncMfaWidget
          aeroPassUserUuid={mfaProps.aeroPassUserUuid}
          token={mfaProps.token}
          jobId={mfaProps.jobId}
          connectionId={mfaProps.connectionId}
          onSuccess={handleMfaSuccess}
          onClose={handleMfaClose}
          onError={handleMfaError}
        />
      )}

      {children}
    </AerosyncPreAuthContext.Provider>
  );
};
